import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import api from '../api';
import PageLayout from '../components/PageLayout';
import { formatDateTime } from '../utils/formatDate';

const ManagementDashboard = () => {
  const { error: toastError } = useToast();

  let adminUser = {};
  try {
    adminUser = JSON.parse(sessionStorage.getItem('adminUser') || '{}');
  } catch (e) {
    console.error("Failed to parse admin session", e);
  }

  const [recentScans, setRecentScans] = useState([]);
  const [scansLoading, setScansLoading] = useState(true);
  const [systemStats, setSystemStats] = useState(null);

  useEffect(() => {
    const fetchSystemStats = async () => {
      try {
        const response = await api.get('/api/admin/system-stats');
        if (response.data.success) {
          setSystemStats(response.data.data);
        }
      } catch (error) {
        console.error('Failed to fetch system stats:', error);
        toastError('Failed to fetch system stats');
      }
    };
    fetchSystemStats();
  }, [toastError]);

  useEffect(() => {
    const fetchRecentScans = async () => {
      try {
        setScansLoading(true);
        const response = await api.get('/api/recent-scans', {
          params: {
            limit: 3,
            userId: adminUser.id
          }
        });
        setRecentScans(response.data.reports || []);
      } catch (error) {
        console.error('Failed to fetch recent scans:', error);
        toastError('Failed to fetch recent scans');
      } finally {
        setScansLoading(false);
      }
    };

    if (adminUser.id) {
      fetchRecentScans();
    } else {
      setScansLoading(false);
    }
  }, [adminUser.id, toastError]);

  const formatDate = (dateString) => formatDateTime(dateString);

  const getGradeBadgeClass = (grade) => {
    switch (grade) {
      case 'A': return 'bg-success bg-opacity-10 text-success';
      case 'B': return 'bg-info bg-opacity-10 text-info';
      case 'C': return 'bg-warning bg-opacity-10 text-warning';
      case 'D': return 'bg-warning bg-opacity-10 text-warning';
      case 'F': return 'bg-danger bg-opacity-10 text-danger';
      default: return 'bg-secondary bg-opacity-10 text-secondary';
    }
  };

  return (
    <PageLayout title="Admin Dashboard">
      <div className="row g-4">
        {/* Recent Scans Card */}
        <div className="col-md-6">
          <div className="card border-0 h-100 shadow-sm">
            <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
              <strong className="card-title mb-0">
                <i className="las la-file-alt me-2"></i>
                Recent Security Scans
              </strong>
              <span className="badge bg-white text-primary rounded-pill">Latest</span>
            </div>
            <div className="card-body">
              {scansLoading ? (
                <div className="text-center py-3">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : recentScans.length > 0 ? (
                <div className="list-group list-group-flush">
                  {recentScans.map((scan) => (
                    <a
                      key={scan.id}
                      href={scan.downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="list-group-item list-group-item-action px-0"
                    >
                      <div className="d-flex w-100 justify-content-between align-items-center">
                        <div>
                          <h6 className="mb-1 text-truncate max-w-[200px]">
                            <i className="las la-globe me-2 text-primary"></i>
                            {scan.domain}
                          </h6>
                          <small className="text-muted">
                            <i className="las la-clock me-1"></i>
                            {formatDate(scan.created_at)}
                          </small>
                        </div>
                        <div className="text-end">
                          <span className={`badge ${getGradeBadgeClass(scan.security_grade)} me-2`}>
                            {scan.security_grade}
                          </span>
                          <span className="badge bg-light text-dark border">
                            {scan.security_score}
                          </span>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-muted">
                  <i className="las la-inbox fa-3x mb-2 opacity-50"></i>
                  <p className="mb-0">No recent scans found.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* User Management */}
        <div className="col-md-6">
          <div className="card border-0 h-100 shadow-sm">
            <div className="card-header bg-danger text-white">
              <strong className="card-title mb-0">
                <i className="las la-users me-2"></i>
                User Management
              </strong>
            </div>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h2 className="display-4 fw-bold mb-0 text-danger">
                    {systemStats?.totalUsers || 0}
                  </h2>
                  <p className="text-muted mb-0">Total Registered Users</p>
                </div>
                <div className="bg-danger bg-opacity-10 p-3 rounded-circle">
                  <i className="las la-users fa-3x text-danger"></i>
                </div>
              </div>
              <div className="d-grid">
                <Link to="/management/users" className="btn btn-outline-danger">
                  <i className="las la-cog me-2"></i>Manage Users
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* System Overview */}
        <div className="col-md-6">
          <div className="card border-0 h-100 shadow-sm">
            <div className="card-header bg-warning text-white">
              <strong className="card-title mb-0">
                <i className="las la-cog me-2"></i>
                System Overview
              </strong>
            </div>
            <div className="card-body">
              <div className="row g-3 mb-4">
                <div className="col-6">
                  <div className="border rounded p-3 text-center bg-light">
                    <h4 className="mb-0">{systemStats?.totalDomains || 0}</h4>
                    <small className="text-muted">Monitored Domains</small>
                  </div>
                </div>
                <div className="col-6">
                  <div className="border rounded p-3 text-center bg-light">
                    <h4 className="mb-0 text-success">Active</h4>
                    <small className="text-muted">System Status</small>
                  </div>
                </div>
              </div>
              <div className="d-grid">
                <Link to="/management/settings" className="btn btn-outline-warning">
                  <i className="las la-sliders-h me-2"></i>Configure Settings
                </Link>
                <Link to="/management/coupons" className="btn btn-outline-primary mt-2">
                  <i className="las la-tags me-2"></i>Manage Coupons
                </Link>
                <Link to="/management/findings" className="btn btn-outline-danger mt-2">
                  <i className="las la-exclamation-triangle me-2"></i>Findings & Remediation
                </Link>
                <Link to="/management/discovered-assets" className="btn btn-outline-info mt-2">
                  <i className="las la-radar me-2"></i>Discovered Assets
                </Link>
              </div>
            </div>
          </div>
        </div>



        {/* Reports & Analytics */}
        <div className="col-md-6">
          <div className="card border-success h-100 shadow-sm">
            <div className="card-header bg-success text-white">
              <strong className="card-title mb-0">
                <i className="las la-chart-bar me-2"></i>
                Reports & Analytics
              </strong>
            </div>
            <div className="card-body">
              <div className="row g-3 mb-4">
                <div className="col-6">
                  <div className="p-3 rounded bg-success bg-opacity-10 text-center">
                    <h3 className="mb-0 text-success">{systemStats?.totalScans || 0}</h3>
                    <small className="text-dark">Total Scans Ran</small>
                  </div>
                </div>
                <div className="col-6">
                  <div className="p-3 rounded bg-info bg-opacity-10 text-center">
                    <h3 className="mb-0 text-info">{systemStats?.avgScore || 0}</h3>
                    <small className="text-dark">Global Avg Score</small>
                  </div>
                </div>
              </div>
              <div className="d-grid gap-2">
                <button className="btn btn-outline-success">
                  <i className="las la-download me-2"></i>Download Full Report
                </button>
                <Link to="/management/logs" className="btn btn-sm btn-link text-muted text-decoration-none text-center">
                  View Activity Logs <i className="las la-arrow-right ms-1"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default ManagementDashboard;